/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Button, Checkbox, CircularProgress, FormControlLabel, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined';
import { useEffect, useState } from "react";
import CustomInput from "../../inputs/customInput";
import RoleService from "../../../services/roleService";
import { useTheme } from "@emotion/react";

const UpdateRoleBox = ({
    handleReload,
    setPopupMsg,
    setPopupType,
    setShowPopup,
    roleId }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [role, setRole] = useState({});
    const [permissions, setPermissions] = useState([]);

    const [formValues, setFormValues] = useState({
        name: role.name,
        permissions: []
    });

    const [updateErrors, setUpdateErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isModalLoading, setIsModalLoading] = useState(true);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    useEffect(() => {
        if (role) {
            setFormValues({
                name: role.name || '',
                permissions: role.rolePermissions ? role.rolePermissions.map(permission => permission.id) : []
            });
        }
    }, [role]);

    const validateForm = () => {
        const newUpdateErrors = {};

        if (formValues.permissions.length === 0) {
            newUpdateErrors.permissions = t('permissionsRequired');
        }

        setUpdateErrors(newUpdateErrors);

        return Object.keys(newUpdateErrors).length === 0;
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLoading) return;

        if (validateForm()) {
            try {
                setIsLoading(true);
                const data = await RoleService.update({
                    id: role.id,
                    ...formValues
                });
                if (data.status === 200) {
                    setPopupMsg(data.data.message);
                    setPopupType('update');
                    setShowPopup(true);
                    handleReload();
                    setFormValues({ name: '', permissions: [] });
                }

                setUpdateErrors({});
            } catch (error) {
                if (error.response?.status === 400) {
                    const responseErrors = error.response.data || {};
                    const formattedErrors = {};

                    Object.keys(responseErrors).forEach((field) => {
                        formattedErrors[field] = responseErrors[field];
                    });

                    setUpdateErrors(formattedErrors);
                } else {
                    setPopupMsg(error.response.data.error);
                    setPopupType('error');
                    console.error("An unexpected error occurred:", error);
                    setUpdateErrors({ general: error.response.data.error });
                }
            } finally {
                setIsLoading(false);
            }
        }
    };

    const fetchPermissions = async () => {
        try {
            const data = await RoleService.fetchPermissions();
            setPermissions(data);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const fetchRole = async () => {
        try {
            const data = await RoleService.fetchOne(roleId);
            setRole(data);
            setIsModalLoading(false);
        } catch (error) {
            console.error('Error fetching permissions:', error);
        }
    };

    const handlePermissions = (permissionId) => {
        setFormValues((prevValues) => {
            const isExist = prevValues.permissions.includes(permissionId);
            const updatedPermissions = isExist
                ? prevValues.permissions.filter((p) => p !== permissionId)
                : [...prevValues.permissions, permissionId];
            return {
                ...prevValues,
                permissions: updatedPermissions
            };
        });
    };

    useEffect(() => {
        setIsModalLoading(true);
        // if (open) {
        fetchPermissions();
        fetchRole();
        // }
    }, []);


    return (
        <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
                width: '100%',
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: 1,
                borderColor: theme.palette.customGrey.borderInput,
                p: isMobile ? 1 : 4,
            }}
        >
            {
                isModalLoading ? <Box sx={{ justifyContent: 'center', display: 'flex', my: 10 }} >
                    <CircularProgress color="primary" size={28} />
                </Box>
                    :
                    <Box>
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'space-between'
                        }}>
                            <Typography id="transition-modal-title" variant="h6" component="h2">
                                {t('updateRole')}
                            </Typography>
                        </Box>
                        <CustomInput
                            label={t('name')}
                            placeholder={t('nameHere')}
                            name="name"
                            type='text'
                            value={formValues.name}
                            error={!!updateErrors.name}
                            helperText={updateErrors.name}
                            onChange={handleChange}
                        />

                        <p
                            style={{
                                color: theme.palette.text.primary,
                                fontSize: 13,
                                marginTop: 24,
                                marginBottom: 4,
                            }}
                        >
                            {t('choosePermissions')}
                        </p>

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '16px',
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            {permissions.map((permission) => (
                                <FormControlLabel
                                    key={permission.id}
                                    control={
                                        <Checkbox
                                            checked={formValues.permissions.includes(permission.id)}
                                            onChange={() => handlePermissions(permission.id)}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: isMobile ? 12 : 16 }}>
                                        {t(permission.name)}
                                    </Typography>}
                                />
                            ))}
                        </Box>

                        {updateErrors.permissions && (
                            <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                                {updateErrors.permissions}
                            </Typography>
                        )}

                        {updateErrors.general && (
                            <Typography align="center" color="error" variant="body2" sx={{ mt: 2 }}>
                                {updateErrors.general}
                            </Typography>
                        )}

                        <Box sx={{
                            display: 'flex', justifyContent: 'center', alignItems: 'center',
                            mt: updateErrors.general ? 2 : 4
                        }}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                sx={{ textTransform: 'none', width: 140, height: 40 }}
                            >
                                {isLoading ? <CircularProgress color="secondary" size={28} /> :
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <p>{t('update')}</p>
                                        <DriveFileRenameOutlineOutlinedIcon fontSize="small" sx={{ mx: 0.4 }} />
                                    </Box>
                                }
                            </Button>
                        </Box>
                    </Box>
            }
        </Box>
    );
}

export default UpdateRoleBox;
